// Lightweight realtime signalling for coach workouts via Supabase Broadcast.
//
// When a coach starts a global workout, their client broadcasts on a shared
// channel keyed by the (shared) planned-training id. Athletes subscribed to that
// channel react instantly by refetching the authoritative join-state from the
// API — so the API stays the source of truth and a spoofed broadcast can't fake
// a start. This is a best-effort accelerator: a reduced poll remains as a
// fallback if a broadcast is ever missed (reconnect/offline).
//
// Broadcast channels need no Supabase dashboard config and no table/RLS setup.

import { supabase } from './supabase';

const channelName = (trainingId: string) => `training:${trainingId}`;
const GLOBAL_START_EVENT = 'global-start';

/** Coach side: notify joined athletes that the workout just started. */
export async function broadcastGlobalStart(trainingId: string): Promise<void> {
  if (!trainingId) return;
  try {
    const channel = supabase.channel(channelName(trainingId));
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => { if (!settled) { settled = true; resolve(); } };
      channel.subscribe((status) => { if (status === 'SUBSCRIBED') finish(); });
      setTimeout(finish, 1500); // don't hang if realtime is unavailable
    });
    await channel.send({
      type: 'broadcast',
      event: GLOBAL_START_EVENT,
      payload: { trainingId },
    });
    setTimeout(() => { void supabase.removeChannel(channel); }, 500);
  } catch (e) {
    // Realtime is optional; the athletes' fallback poll still catches the start.
    console.warn('[realtime] broadcastGlobalStart failed', e);
  }
}

/**
 * Athlete side: run `onStart` whenever a coach broadcasts a start for this
 * training. Returns an unsubscribe function.
 */
export function subscribeGlobalStart(trainingId: string, onStart: () => void): () => void {
  if (!trainingId) return () => {};
  let channel: ReturnType<typeof supabase.channel> | null = null;
  try {
    channel = supabase
      .channel(channelName(trainingId))
      .on('broadcast', { event: GLOBAL_START_EVENT }, () => onStart())
      .subscribe();
  } catch (e) {
    console.warn('[realtime] subscribeGlobalStart failed', e);
  }
  return () => {
    if (channel) { void supabase.removeChannel(channel); }
  };
}

const userChannelName = (userId: string) => `user:${userId}`;

/**
 * Subscribe to this user's personal channel. Any broadcast on it (e.g. a coach
 * started a booked workout) runs `onMessage` — the Header uses it to refetch
 * notifications instantly instead of polling. Returns an unsubscribe function.
 */
export function subscribeUserChannel(userId: string, onMessage: () => void): () => void {
  if (!userId) return () => {};
  let channel: ReturnType<typeof supabase.channel> | null = null;
  try {
    channel = supabase
      .channel(userChannelName(userId))
      .on('broadcast', { event: '*' }, () => onMessage())
      .subscribe();
  } catch (e) {
    console.warn('[realtime] subscribeUserChannel failed', e);
  }
  return () => {
    if (channel) { void supabase.removeChannel(channel); }
  };
}

/** Send a best-effort notification to each given user's personal channel. */
export async function notifyUsers(userIds: string[], event: string): Promise<void> {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  await Promise.all(ids.map(async (uid) => {
    try {
      const channel = supabase.channel(userChannelName(uid));
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => { if (!settled) { settled = true; resolve(); } };
        channel.subscribe((status) => { if (status === 'SUBSCRIBED') finish(); });
        setTimeout(finish, 1500);
      });
      await channel.send({ type: 'broadcast', event, payload: {} });
      setTimeout(() => { void supabase.removeChannel(channel); }, 500);
    } catch {
      // best effort; the recipient's fallback poll still catches it
    }
  }));
}
